import { Avatar, Button, Dropdown, Flex, Layout, Typography } from 'antd';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/ChevronDown.svg';
import { ReactComponent as PersonCircleIcon } from 'assets/icons/PersonCircle.svg';
import { ReactComponent as HellioLogo } from 'assets/logo/logo-blue.svg';
import clsx from 'clsx';
import AccountLogo from 'components/AccountLogo';
import NewDossierButton from 'components/NewDossierButton';
import SideMenu from 'components/SideMenu';
import { useAuthContext } from 'contexts/AuthContext';
import { ContactPopover } from 'layouts/Component/ContactPopover';
import { NotificationPopover } from 'layouts/Component/NotificationPopover';
import type { MenuInfo } from 'rc-menu/lib/interface';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

const { Header, Content } = Layout;

const AppLayout = (props: { children: React.ReactNode }) => {
  const { children } = props;
  const { auth, logout } = useAuthContext();
  const { t } = useTranslation();
  const history = useHistory();
  const [collapsed, setCollapsed] = React.useState(false);

  const displayName = `${auth?.user?.firstname} ${auth?.user?.lastname}`;

  const handleLogout = useCallback(() => {
    logout();
    history.push('/connexion');
  }, [history, logout]);

  const handleMenuClick = useCallback(
    (info: MenuInfo) => {
      if (info?.key === 'logout') {
        handleLogout();
      } else if (typeof info?.key === 'string') {
        history.push(info?.key);
      }
    },
    [history, handleLogout],
  );

  const userMenuItems = [
    {
      key: '/mon-compte',
      label: t('global:my_account', 'Mon compte'),
    },
    {
      key: 'logout',
      label: t('global:logout'),
    },
  ];

  const isImpersonated = !!auth?.user?.impersonated;

  return (
    <Layout
      className="print:bg-white"
      style={{
        minHeight: '100vh',
      }}
    >
      {isImpersonated && (
        <div
          className={
            'print:hidden fixed top-0 left-0 right-0 bg-gray-800 flex items-center justify-center gap-6 h-[52px] z-[1000]'
          }
        >
          <Typography.Paragraph className={'!m-0 text-white'}>
            {t('login:impersonate', {
              name: displayName,
            })}
          </Typography.Paragraph>
          <Button onClick={handleLogout} type={'primary'}>
            {t('global:logout')}
          </Button>
        </div>
      )}
      <Header
        className={clsx(
          'print:hidden flex justify-between fixed w-full z-[1000] px-4 bg-white shadow-sm h-16',
          {
            'top-[52px]': isImpersonated,
            'top-0': !isImpersonated,
          },
        )}
      >
        <div className="flex items-center gap-2">
          <HellioLogo />
          <AccountLogo />
        </div>

        <div className="flex items-center gap-3">
          <ContactPopover />

          <NewDossierButton />

          <Dropdown
            menu={{
              items: userMenuItems,
              onClick: handleMenuClick,
            }}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="large"
              className="flex items-center gap-2 hover:!bg-white"
            >
              <Avatar className="!flex items-center justify-center">
                <PersonCircleIcon className="text-black" />
              </Avatar>
              <ChevronDownIcon className="text-bleu-hellio" />
            </Button>
          </Dropdown>

          <NotificationPopover />
        </div>
      </Header>

      <Layout
        className={clsx(collapsed ? 'ml-[80px]' : 'ml-[205px]', {
          'pt-[64px]': !isImpersonated,
          'pt-[116px]': isImpersonated,
        })}
      >
        <SideMenu
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          isImpersonated={isImpersonated}
        />
        <Content
          className={clsx('flex print:p-0 px-4 py-4 print:mt-0 relative')}
        >
          <Flex vertical className="grow w-full mx-auto">
            {children}
          </Flex>
        </Content>
      </Layout>
    </Layout>
  );
};

export default React.memo(AppLayout);
